import { useEffect, useState } from "react";
import { Check, X, Ban, Trash2 } from "lucide-react";
import { api, eur, fmtError } from "../lib/api";
import { toast } from "sonner";

export const AdminArtists = () => {
  const [artists, setArtists] = useState([]);
  const [rate, setRate] = useState("");
  const [savedRate, setSavedRate] = useState(null);

  const load = () => {
    api.get("/admin/artists").then((r) => setArtists(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => {
      setRate(String(r.data.commission_rate));
      setSavedRate(r.data.commission_rate);
    }).catch(() => {});
  };
  useEffect(load, []);

  const setStatus = async (a, status) => {
    try {
      await api.post(`/admin/artists/${a.user_id}/status`, { status });
      toast.success(
        status === "approved" ? `${a.artist_name} validé — ses morceaux sont en ligne`
        : status === "deactivated" ? `${a.artist_name} désactivé — ses morceaux sont masqués`
        : `${a.artist_name} refusé`
      );
      load();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const saveRate = async () => {
    try {
      const { data } = await api.patch("/admin/settings", { commission_rate: parseFloat(rate) });
      setSavedRate(data.commission_rate);
      toast.success(`Commission fixée à ${data.commission_rate}%`);
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const deleteArtist = async (a) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${a.artist_name} ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/artists/${a.user_id}`);
      toast.success(`${a.artist_name} supprimé définitivement`);
      load();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const pending = artists.filter((a) => a.artist_status === "pending");
  const others = artists.filter((a) => a.artist_status !== "pending");

  return (
    <section className="border border-white/[0.08] bg-surface p-8" data-testid="admin-artists">
      <h2 className="font-display text-2xl font-medium">Artistes & commission</h2>

      <div className="mt-6 border border-white/[0.08] p-5">
        <label className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          Commission plateforme (%)
        </label>
        <div className="flex gap-3">
          <input
            type="number"
            min="0"
            max="50"
            step="0.5"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            data-testid="commission-input"
            className="w-28 border border-white/15 bg-transparent px-4 py-3 text-sm outline-none focus:border-amber-500"
          />
          <button
            onClick={saveRate}
            data-testid="commission-save"
            className="border border-amber-500/60 px-5 text-xs uppercase tracking-[0.2em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black"
          >
            Enregistrer
          </button>
        </div>
        <p className="mt-2 text-[11px] text-zinc-500">
          {savedRate !== null && `Actuellement ${savedRate}% — le reste est reversé à l'artiste. S'applique aux prochaines ventes.`}
        </p>
        <p className="mt-1 text-[11px] text-zinc-600">
          Votre compte PHP MUSIC n'est pas commissionné (0 % — vous touchez 100 % de vos ventes).
        </p>
      </div>

      {pending.length > 0 && (
        <div className="mt-6">
          <p className="text-xs uppercase tracking-[0.25em] text-amber-500">En attente de validation</p>
          <div className="mt-3 space-y-3" data-testid="artists-pending">
            {pending.map((a) => (
              <div key={a.user_id} className="flex flex-wrap items-center gap-3 border border-amber-500/30 bg-amber-500/5 p-4" data-testid={`artist-pending-${a.user_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-base font-medium">{a.artist_name}</p>
                  <p className="text-[11px] text-zinc-500">{a.email}</p>
                </div>
                <button
                  onClick={() => setStatus(a, "approved")}
                  data-testid={`approve-${a.user_id}`}
                  className="flex items-center gap-2 border border-emerald-500/50 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-emerald-400 transition-colors hover:bg-emerald-500 hover:text-black"
                >
                  <Check className="h-3.5 w-3.5" /> Valider
                </button>
                <button
                  onClick={() => setStatus(a, "rejected")}
                  data-testid={`reject-${a.user_id}`}
                  className="flex items-center gap-2 border border-red-500/50 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-red-400 transition-colors hover:bg-red-500 hover:text-black"
                >
                  <X className="h-3.5 w-3.5" /> Refuser
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Tous les artistes</p>
        <div className="mt-3 space-y-2" data-testid="artists-list">
          {others.length === 0 && pending.length === 0 && (
            <p className="text-sm text-zinc-500">Aucun artiste inscrit pour le moment.</p>
          )}
          {others.map((a) => (
            <div key={a.user_id} className="flex flex-wrap items-center gap-3 border border-white/[0.06] p-4 text-sm" data-testid={`artist-row-${a.user_id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-display font-medium">{a.artist_name}</p>
                <a href={`mailto:${a.email}`} data-testid={`artist-email-${a.user_id}`} className="block truncate text-[11px] text-amber-400/90 transition-colors hover:text-amber-300">
                  {a.email}
                </a>
                <p className="text-[11px] text-zinc-500">
                  {a.track_count} morceau{a.track_count > 1 ? "x" : ""} · {a.sales_count} vente{a.sales_count > 1 ? "s" : ""} · {eur(a.sales_gross)} bruts
                </p>
              </div>
              <span className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] ${
                a.artist_status === "approved" ? "text-emerald-400" : a.artist_status === "deactivated" ? "text-zinc-500" : "text-red-400"
              }`}>
                {a.artist_status === "approved" ? "Validé" : a.artist_status === "deactivated" ? "Désinscrit" : "Refusé"}
              </span>
              {a.artist_status === "approved" && (
                a.contract_signed ? (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/artists/${a.user_id}/contract`}
                    data-testid={`contract-${a.user_id}`}
                    className="text-[11px] uppercase tracking-[0.15em] text-emerald-400 underline-offset-4 hover:underline"
                  >
                    Contrat reçu
                  </a>
                ) : (
                  <span className="text-[11px] uppercase tracking-[0.15em] text-amber-400/80" data-testid={`contract-missing-${a.user_id}`}>
                    Contrat en attente
                  </span>
                )
              )}
              {a.artist_status === "approved" && (
                <button
                  onClick={() => {
                    if (window.confirm(`Désactiver ${a.artist_name} ? Ses morceaux et sa page publique seront masqués (réversible avec « Réactiver »).`)) {
                      setStatus(a, "deactivated");
                    }
                  }}
                  data-testid={`deactivate-${a.user_id}`}
                  className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-red-400"
                >
                  <Ban className="h-3.5 w-3.5" /> Désactiver
                </button>
              )}
              {(a.artist_status === "rejected" || a.artist_status === "deactivated") && (
                <>
                  <button
                    onClick={() => setStatus(a, "approved")}
                    data-testid={`reapprove-${a.user_id}`}
                    className="text-[11px] uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-amber-400"
                  >
                    {a.artist_status === "deactivated" ? "Réactiver" : "Revalider"}
                  </button>
                  <button
                    onClick={() => deleteArtist(a)}
                    data-testid={`delete-artist-${a.user_id}`}
                    className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-zinc-500 transition-colors hover:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Supprimer
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
